import { useTranslation, untranslated } from "@i18n";
import { useDispatch, useSelector } from "react-redux";
import { useState, useEffect } from "react";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AttachIssues from "./AttachIssues/AttachIssues";
import AttachFiles from "./AttachFiles/AttachFiles";
import { showToastNotification } from "@Reducers/toastSlice";
import { createFileShortcut, consumeExternalFiles } from "@Actions/fileUpload";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, TOAST_TYPE_WARNING } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import FileManager from "./FileManagerNew";
import { Box, CircularProgress } from "@mui/material";
import IconComponent from "@CubeComponents/icons";
import { getFileIcon } from "@CubeComponents/constants/globalFunctions";
import { FilesGrid } from "@Features/files";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import SelectSpaces from "@Components/WorkspaceList/SelectSpaces";
import type { Space } from "@Components/WorkspaceList/useSelectSpaces";
import Text from "@CubeComponents/html/Text/Text";
import useFileManagerAsPopupShared from "./useFileManagerAsPopupShared";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { RootState } from "@Reducers/index";
interface FileManagerAsPopupProps {
  handleFileSelectedButtonClick?: any;
  handleFileSelectedCancelClick?: any;
  handleFolderSelected?: any;
  allowFolderSelectionCallback?: any; // default: false
  workspaceId?: any;
  id?: any;
  openThisSpaceByDefault?: any; // default: false
  hideIssues?: any; // default: false
  hideTasks?: any; // default: false
  hideReviews?: any; // default: false
  hideEstimates?: any; // default: false
  hideExternalSpace?: any; // default: false
  hideClashTests?: any; // default: false
  createShortcut?: any; // default: false
  consumeFiles?: any; // default: false
  hideSchedules?: any; // default: false
  hideContracts?: any; // default: false
  hideMergeFiles?: any; // default: false
  enableOnlyShareableResources?: any; // default: false
  shortCutData?: any; // default: {}
  hideAllTabs?: any; // default: false
  useAttachedExternalVolume?: any; // default: false
  heading?: any;
  allowInput?: any;
  inputLabel?: any;
  isTransmittal?: any;
  externalFolderData?: any;
  disableFiles?: any;
  minimumFiles?: any;
  errorMessage?: any;
  showOnlyMergedFile?: any;
  addEmptyContainerToList?: any;
  enableFilesTypes?: any;
  hideShortcuts?: boolean; // default: false
  createMerge?: any; // default: false
  attachSelectedBtnName?: any; // default: null
  selectedFilesData?: any; // default: []
  disableMultipleSelection?: any;
  projectId?: any;
  enableSpaceListDropdown?: any;
  setSpaceId?: any;
  disableContextMenu?: any; // default: false
  setIsLoadingCreateButton?: any;
  isLoadingCreateButton?: any;
  isLoadingAddFiles?: any;
  setIsLoadingAddFiles?: any;
  allowSwitchSpace?: boolean;
  hideFilesWithoutVersion?: boolean;
  allowViewToggle?: boolean;
  // Constrained list for the header Space dropdown. When provided, the dropdown shows only these
  // spaces (instead of fetching every project space) and switching is enabled if there is >1 option.
  spaceListDataSource?: Space[] | null;
}
function FileManagerAsPopup({
  handleFileSelectedButtonClick,
  handleFileSelectedCancelClick,
  handleFolderSelected,
  allowFolderSelectionCallback = false,
  workspaceId,
  openThisSpaceByDefault = false,
  hideIssues = false,
  hideTasks = false,
  hideReviews = false,
  hideEstimates = false,
  hideExternalSpace = false,
  hideClashTests = false,
  createShortcut = false,
  consumeFiles = false,
  hideSchedules = false,
  hideContracts = false,
  hideMergeFiles = false,
  enableOnlyShareableResources = false,
  shortCutData = {},
  hideAllTabs = true,
  useAttachedExternalVolume = false,
  heading,
  allowInput,
  inputLabel,
  isTransmittal,
  externalFolderData,
  disableFiles,
  minimumFiles,
  errorMessage,
  showOnlyMergedFile,
  addEmptyContainerToList,
  enableFilesTypes,
  hideShortcuts = false,
  createMerge = false,
  attachSelectedBtnName = null,
  selectedFilesData = [],
  disableMultipleSelection,
  projectId,
  setSpaceId,
  disableContextMenu = false,
  setIsLoadingCreateButton,
  isLoadingCreateButton,
  isLoadingAddFiles,
  setIsLoadingAddFiles,
  allowSwitchSpace = false,
  allowViewToggle = true,
  hideFilesWithoutVersion = false,
  spaceListDataSource,
}: Readonly<FileManagerAsPopupProps>) {
  const { t } = useTranslation();
  const { layout } = useSelector((state: RootState) => state);
  const CDEPLAN = layout.SPACES?.ENABLE;
  const PMPLAN = layout.FILES?.ENABLE;

  const dispatch = useDispatch();
  const fileManagerHeading = heading || "Attach Files";

  // Use shared hook for file selection logic (platform-agnostic)
  const {
    listOfSelectedFiles,
    setListOfSelectedFiles,
    selectedFolderData,
    inputValue,
    setInputValue,
    issuesHavingNoFiles,
    AddToListDisabled,
    disableAttachButton,
    disableCreateShortcutButton,
    handleFileSelectionCallback,
    handleAddToSelection,
    handleRemoveOfSelectedFile,
    handleFileSelectedButtonClickCallback,
    handleFolderSelectionCallback,
    handleFolderSelectedCallback,
    removeIssuesWithNoFiles,
    acceptFilesForIssues,
    // View mode state
    toggleFilesAsList,
    handleToggleFilesAsList,
    showViewToggle,
    // Space switching state
    switchSpaceId,
    showNoSpaceWarning,
    setShowNoSpaceWarning,
    handleSpaceListFetch,
    handleSpaceSelect,
  } = useFileManagerAsPopupShared({
    handleFileSelectedButtonClick,
    handleFolderSelected,
    minimumFiles,
    allowInput,
    disableMultipleSelection,
    isTransmittal,
    addEmptyContainerToList,
    createMerge,
    createShortcut,
    selectedFilesData,
    setIsLoadingCreateButton,
    setIsLoadingAddFiles,
    workspaceId,
    hasCDEPlan: !!CDEPLAN,
    hasPMPlan: !!PMPLAN,
    allowViewToggle,
  });

  // Web-specific state
  const [openAttachIssuesModal, setOpenAttachIssuesModal] = useState(false);
  const [filesIssues] = useState([]);
  const [newState, setNewState] = useState<any>(false);
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);

  // When a constrained space list is supplied, useSelectSpaces skips the project-spaces fetch, so the
  // fetch callback that normally clears the loading state never fires. Resolve the warning flag here
  // (mirrors handleSpaceListFetch: warn only when there is neither a workspace nor any space option).
  useEffect(() => {
    if (spaceListDataSource) {
      setShowNoSpaceWarning(!workspaceId && spaceListDataSource.length === 0);
    }
  }, [spaceListDataSource, workspaceId, setShowNoSpaceWarning]);

  const handleSetOpenAttachIssuesModal = (val: any) => {
    setOpenAttachIssuesModal(val);
  };

  const onAttachIssuesCallback = (data: any) => {
    let filteredFilesIssues: any = [];
    const currentVersion = filesIssues.reduce(
      (acc: any, curr: any) => (acc > curr.fileVersionNumber ? acc : curr.fileVersionNumber),
      0,
    );

    if (data.selectedIssueType.includes("open")) {
      filesIssues.forEach((file: any) => {
        if (file.status.toLowerCase() == "open") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType.includes("in_review")) {
      filesIssues.forEach((file: any) => {
        if (file.status.toLowerCase() == "in_review") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType.includes("closed")) {
      filesIssues.forEach((file: any) => {
        if (file.status.toLowerCase() == "closed") {
          filteredFilesIssues.push(file);
        }
      });
    }
    if (data.selectedIssueType.length == 0) {
      filteredFilesIssues = filesIssues;
    }

    if (data.selectedVersionType == "current_version") {
      filteredFilesIssues = filteredFilesIssues.filter((file: any) => file.fileVersionNumber == currentVersion);
    } else if (data.selectedVersionType == "previous_version") {
      filteredFilesIssues = filteredFilesIssues.filter((file: any) => file.fileVersionNumber != currentVersion);
    } else if (data.selectedVersionType == "") {
      filteredFilesIssues = [];
    }

    const selectedFilesToObj: any = { ...listOfSelectedFiles };
    filteredFilesIssues.map((file: any) => {
      selectedFilesToObj[file.issueVersionId] = file;
    });
    setListOfSelectedFiles(selectedFilesToObj);
  };

  // handleCancelClickOnAttachFiles and handleAcceptClickOnAttachFiles are now in shared hook
  // as removeIssuesWithNoFiles and acceptFilesForIssues

  const handleCreateShortcut = async () => {
    setSubmitButtonLoading(true);
    const data = {
      ...shortCutData,
      spaceId: selectedFolderData.spaceId,
      parentId: selectedFolderData.hierarchyId,
      folderType: selectedFolderData.folderType,
    };
    const res = await createFileShortcut(data);
    handleFileSelectedCancelClick();
    if (!res.data.status) {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage as any),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: t("Shortcut created successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    setSubmitButtonLoading(false);
  };

  const handleConsumeFiles = async (data?: any) => {
    setSubmitButtonLoading(true);
    if (!data) {
      data = {
        fileVersionIds: shortCutData.map((x: any) => ({
          fileVersion: x.fileVersion,
          uploadNewVersion: false,
        })),
        spaceId: selectedFolderData.spaceId,
        parentId: selectedFolderData.hierarchyId,
        folderType: selectedFolderData.folderType,
        skipDuplicateCheck: false,
        transmittalData: externalFolderData,
      };
    }
    const res = await consumeExternalFiles(data);
    if (res.data.status) {
      if (res.data.duplicatesFound) {
        dispatch(
          showToastNotification({
            content: t("Duplicate file(s) found so files were not consumed."),
            type: TOAST_TYPE_ERROR,
          }),
        );
        setNewState({
          response: res.data,
          payload: data,
        });
      } else {
        dispatch(
          showToastNotification({
            content: t("File(s) consumed successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      }
    } else if (!res.data.status) {
      if (res.data.errorCode) {
        dispatch(
          showToastNotification({
            content: t("Some Error(s) occured. Please check Escrow files section."),
            type: TOAST_TYPE_WARNING,
          }),
        );
      } else {
        dispatch(
          showToastNotification({
            content: untranslated(res.data.errorMessage as any),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    }
    setSubmitButtonLoading(false);
    handleFileSelectedCancelClick();
  };

  const handleDuplicateClick = async (button: any) => {
    const newPayload = {
      ...newState.payload,
      skipDuplicateCheck: true,
    };
    newPayload.fileVersionIds = newPayload.fileVersionIds.map((x: any) => ({
      ...x,
      [button]: true,
    }));
    setNewState(false);
    await handleConsumeFiles(newPayload);
  };

  function footer() {
    return (
      <div className="flex flex-row gap-6 py-4 px-6 justify-end">
        <ButtonComponent
          onClick={() => handleDuplicateClick("uploadNewVersion")}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        >
          <Text>{t("Upload all as new versions")}</Text>
        </ButtonComponent>

        <ButtonComponent
          onClick={() => handleDuplicateClick("uploadNewFile")}
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
        >
          <Text>{t("Upload all as new files")}</Text>
        </ButtonComponent>
      </div>
    );
  }

  function modelContent() {
    return (
      <div className="my-5">{`${newState.response.duplicatesFound} files have duplicates at the selected location. How do you want to proceed?`}</div>
    );
  }

  function popUpContent() {
    return (
      <div className="flex flex-col p-6 gap-6 overflow-auto h-full">
        <div className="flex flex-col gap-6 p-4 border border-button_light rounded-lg">
          {!showNoSpaceWarning && (
            <>
              {(showViewToggle || allowInput) && (
                <div className="flex flex-row gap-4 items-center mx-1">
                  {allowInput && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center">
                        <span>{inputLabel}</span>
                        <div className="ps-4">
                          <TextFieldComponent
                            type="text"
                            name="mergedFileName"
                            placeholder={t("Add name here")}
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            slotProps={{
                              htmlInput: {
                                "data-testid": "addMergeFileName",
                              },
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {showViewToggle && (
                    <div className="flex items-center gap-2">
                      <ToggleSwitchComponent checked={!toggleFilesAsList} onChange={handleToggleFilesAsList} />
                      {toggleFilesAsList ? (
                        <Text className="text-sm">{t("Seeing files as a list")}</Text>
                      ) : (
                        <Text className="text-sm">{t("Seeing files as folder structure")}</Text>
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          {getContent()}
          {!showNoSpaceWarning && (
            <>
              {!createShortcut && (
                <div className="col-start-2 row-start-1">
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                    onClick={handleAddToSelection}
                    disabled={AddToListDisabled}
                    data-testid="addFilesToSelectionBelowBtn"
                  >
                    <div className="flex flex-row gap-2 items-center">
                      <Text>{t("Add to Selection Below")}</Text>
                    </div>
                  </ButtonComponent>
                </div>
              )}
            </>
          )}
        </div>
        {!showNoSpaceWarning && (
          <div className="flex justify-between overflow-auto max-h-[15.625rem] flex-shrink-0">
            {!createShortcut && (
              <div className="flex flex-col w-full border border-button_light rounded-lg">
                <Text className="flex p-4  w-full border-b border-button_light font-semibold text-base">
                  {t("Selection")}
                </Text>
                <div className="flex flex-col gap-4 p-4 w-full">
                  <div>
                    <ButtonComponent
                      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
                      disabled={Object.keys(listOfSelectedFiles).length === 0}
                      onClick={() => setListOfSelectedFiles({})}
                    >
                      <Text>{t("Remove All")}</Text>
                    </ButtonComponent>
                  </div>
                  <div className="flex gap-5 flex-row overflow-auto">
                    {Object.keys(listOfSelectedFiles).length === 0 ? (
                      <Text className="text-sm leading-[120% font-semibold text-yet_to_start_communication py-2">
                        {t("No File(s) Added")}
                      </Text>
                    ) : (
                      Object.keys(listOfSelectedFiles).map((file) => {
                        if (
                          [
                            "RESOURCE_TYPE_FILE",
                            "RESOURCE_TYPE_MERGED_FILE",
                            "RESOURCE_TYPE_ESTIMATE",
                            "RESOURCE_TYPE_EMBED",
                          ].includes(listOfSelectedFiles[file].resourceType) ||
                          Object.hasOwn(listOfSelectedFiles[file], "dev")
                        ) {
                          return (
                            <div
                              key={file}
                              className="flex border border-stroke_primary gap-2 p-1.5 rounded items-center flex-shrink-0"
                            >
                              <div className="w-4 h-4 flex items-center justify-center">
                                {getFileIcon(listOfSelectedFiles[file].name || listOfSelectedFiles[file].fileName)}
                              </div>
                              <div className="text-sm leading-[120%]">
                                {listOfSelectedFiles[file].name || listOfSelectedFiles[file].fileName}
                              </div>
                              <IconComponent
                                name="Cancel"
                                onClick={() => handleRemoveOfSelectedFile(listOfSelectedFiles[file])}
                                width="1rem"
                                height="1rem"
                              />
                            </div>
                          );
                        }
                        return null;
                      })
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {errorMessage && (
          <div className="ps-4 pt-4 text-[var(--negative-communication)]">
            <span>{errorMessage}</span>
          </div>
        )}
      </div>
    );
  }

  function popUpFooter() {
    return (
      <div className="flex flex-row gap-6 py-4 px-6">
        {!createShortcut && !showNoSpaceWarning && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={handleFolderSelected ? handleFolderSelectedCallback : handleFileSelectedButtonClickCallback}
            disabled={disableAttachButton}
            loading={isLoadingCreateButton || isLoadingAddFiles}
            data-testid="attachSelectedBtn"
          >
            {attachSelectedBtnName || t("Attach")}
          </ButtonComponent>
        )}
        {createShortcut && !consumeFiles && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={handleCreateShortcut}
            disabled={disableCreateShortcutButton}
          >
            <Text>{t("Create here")}</Text>
          </ButtonComponent>
        )}
        {consumeFiles && (
          <ButtonComponent
            buttonType="gradientButton"
            onClick={() => handleConsumeFiles()}
            loading={submitButtonLoading}
            disabled={disableCreateShortcutButton}
          >
            <Text>{t("Consume here")}</Text>
          </ButtonComponent>
        )}
        <ButtonComponent onClick={handleFileSelectedCancelClick} data-testid="cancelSelectedFileBtn">
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );
  }

  const getContent = () => {
    if (showNoSpaceWarning == null) {
      return (
        <div className="flex justify-center h-full flex-col gap-2 items-center">
          <CircularProgress />
        </div>
      );
    } else if (showNoSpaceWarning) {
      return (
        <Text className="flex justify-center h-full flex-col gap-2 items-center">
          <IconComponent name="InspectionFormNoData" width="18.75rem" height="18.75rem" />
          {t("No Spaces available in this project")}
        </Text>
      );
    } else if (!showNoSpaceWarning) {
      return (
        <div className={`flex border border-button_light rounded-lg h-[27.75rem] overflow-auto`}>
          {toggleFilesAsList ? (
            <FilesGrid
              initialShowAllVersions={false}
              spaceId={allowSwitchSpace ? null : switchSpaceId}
              projectId={projectId}
              showTitle={false}
              showToolbar={{ views: true, group: false, upload: false, action: false, pinnedColumns: false }}
              propertiesPanel={false}
              disableMenuOptions
              excelExport={false}
              handleFileSelectionCallback={handleFileSelectionCallback}
              showOnlyMergedFile={showOnlyMergedFile}
              disableFiles={disableFiles}
            />
          ) : (
            <FileManager
              projectId={projectId}
              spaceId={switchSpaceId || ""}
              fileManagerAsPopup
              openThisSpaceByDefault={openThisSpaceByDefault}
              handleFileSelectionCallback={handleFileSelectionCallback}
              handleFolderSelectionCallback={handleFolderSelectionCallback}
              allowFolderSelectionCallback={allowFolderSelectionCallback}
              hideIssues={hideIssues}
              hideTasks={hideTasks}
              hideReviews={hideReviews}
              hideAllTabs={hideAllTabs}
              hideEstimates={hideEstimates}
              hideSchedules={hideSchedules}
              hideContracts={hideContracts}
              hideMergeFiles={hideMergeFiles}
              hideExternalSpace={hideExternalSpace}
              useAttachedExternalVolume={useAttachedExternalVolume}
              hideClashTests={hideClashTests}
              // enablePersistence={false}
              createShortcut={createShortcut}
              disableFiles={disableFiles}
              enableFilesTypes={enableFilesTypes}
              hideShortcuts={hideShortcuts}
              hideUpload
              showOnlyMergedFile={showOnlyMergedFile}
              alreadySelected={selectedFilesData}
              setSpaceId={setSpaceId}
              disableContextMenu={disableContextMenu}
              enableOnlyShareableResources={enableOnlyShareableResources}
              disableDragAndDropUpload={true}
              hideFilesWithoutVersion={hideFilesWithoutVersion}
            />
          )}
        </div>
      );
    } else return "";
  };
  return (
    <>
      <div>
        {openAttachIssuesModal && (
          <Box
            onClick={(e: { stopPropagation: () => void }) => {
              e.stopPropagation();
            }}
          >
            <AttachIssues
              handleSetOpenAttachIssuesModal={handleSetOpenAttachIssuesModal}
              onSuccess={onAttachIssuesCallback}
            />
          </Box>
        )}
        {issuesHavingNoFiles.length > 0 && (
          <Box
            onClick={(e: { stopPropagation: () => void }) => {
              e.stopPropagation();
            }}
          >
            <AttachFiles onCancelClick={removeIssuesWithNoFiles} onAcceptClick={acceptFilesForIssues} />
          </Box>
        )}
        <ModalStateDialogComponent
          modalPadding={0}
          headerTitle={
            <div className="flex flex-row ps-6 py-4 w-full justify-between items-center">
              <ToolTipComponent content={fileManagerHeading}>
                <span className="text-2xl font-semibold truncate max-w-[50%]" data-testid="fileManagerHeading">
                  {fileManagerHeading}
                </span>
              </ToolTipComponent>
              {!showNoSpaceWarning && !(showViewToggle && toggleFilesAsList) && (
                <SelectSpaces
                  disablePermissionCheck
                  projectId={projectId}
                  workspaceId={switchSpaceId}
                  dataSource={spaceListDataSource}
                  isDisabled={
                    toggleFilesAsList ||
                    (PMPLAN && !CDEPLAN) ||
                    (!allowSwitchSpace && (spaceListDataSource?.length ?? 0) <= 1)
                  }
                  selectedSpaceCallback={handleSpaceSelect}
                  fetchSpaceListCallback={handleSpaceListFetch}
                />
              )}
            </div>
          }
          footerContent={popUpFooter()}
          modelContent={popUpContent()}
          isVisible
          width="90%"
          height="90%"
          onCancel={handleFileSelectedCancelClick}
        />
      </div>
      {newState && (
        <ModalStateDialogComponent
          headerTitle={t("Duplicates found")}
          footerContent={footer()}
          modelContent={modelContent()}
          isVisible={newState}
          width="40%"
          onCancel={() => setNewState(false)}
        />
      )}
    </>
  );
}

export default FileManagerAsPopup;
